import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import moment from 'moment';
import { toast } from 'react-toastify';

import { cohortActions, sessionActions } from '../../../store';
import { useProcedureTimeRestrictions } from '../../../hooks';
import {
  areUserRolesIn,
  combineMomentDateTime,
  history,
} from '../../../helpers';
import { getUtcOffset } from '../../../utils/common';
import { FORTA_BLACK, FORTA_ERROR } from '../../../utils/colors';
import {
  BcbaAttendance,
  BTSessionStatus,
  ClinicalRelationships,
  Roles,
} from '../../../constants';
import { FeatureFlags } from '../../../constants/featureFlags';
import {
  DeliveryMethods,
  DEFAULT_SESSION_DURATION,
  DurationOptions,
  SESSION_NOTE_VERSIONS,
} from './constants';
import { EVV_TOKEN_KINDS } from '../constants';
import {
  ExistingSessionInfoFields,
  NewSessionInfoFields,
  SessionInfoDialogs,
} from './components';
import {
  useEvvVerification,
  useSelectedClientDetailsLoader,
  useSessionInfoStoreData,
  useUpdatedDeliveryMethod,
} from './hooks';
import {
  shouldDisableSessionStartByClientBay,
  getActiveBayAssignmentAtDateTime,
  getNumberOfBcbaProviderAssignments,
  getShowBcbaAttendanceField,
  getCurrentClientCohortAssignments,
  getDeliveryMethodOptions,
} from './utils/general';
import AttentionIcon from '../../../assets/images/attention.svg';
import { ClinicalStatus } from '../../../admin/constants/general';
import { BAYS } from '../../../constants';

import type { Moment } from 'moment';
import type {
  IBTSession,
  IClient,
  ISessionPracticedTargetTrial,
} from '../../../utils/types';
import type { Locations } from './constants';
import type { ISessionInfoProps } from './types';

function SessionInfo({
  isEditable,
  setIsSessionStartLocked,
  setEvvQrDialog,
}: ISessionInfoProps) {
  const { getAccessTokenSilently } = useAuth0();
  const sessionNotesV2Enabled = useBooleanFlagValue(
    FeatureFlags.SESSION_NOTES_V2_ENABLED,
    false,
  );
  const {
    clientCohortAssignments,
    clients,
    cohortAssignmentLoading,
    dispatch,
    narrativeSuggestionLoading,
    providerAssignments,
    session,
    sessionLoading,
    user,
  } = useSessionInfoStoreData();

  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [pendingSelectedClientId, setPendingSelectedClientId] = useState<number | null>(null);
  // V2 new sessions start with no duration so the user must pick one; V1 keeps its default.
  const newSessionDefaultDuration = sessionNotesV2Enabled ? null : DEFAULT_SESSION_DURATION;
  const [selectedDuration, setSelectedDuration] = useState<number | null>(
    newSessionDefaultDuration,
  );
  const [bcbaAttendanceStatus, setBcbaAttendanceStatus] = useState<boolean | null>(null);
  const [location, setLocation] = useState<Locations | null>(null);
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethods>(
    areUserRolesIn(user?.roles, [Roles.Vbt]) || areUserRolesIn(user?.roles, [Roles.Bcba])
      ? DeliveryMethods.TELEHEALTH
      : DeliveryMethods.IN_PERSON,
  );
  const [startSessionLoading, setStartSessionLoading] = useState<boolean>(false);
  const [sessionStartDate, setSessionStartDate] = useState<Moment | null>(null);
  const [timeChanged, setTimeChanged] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [openClientConfirm, setOpenClientConfirm] = useState<boolean>(false);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState<boolean>(false);
  const [timeStartValue, setTimeStartValue] = useState<Moment | null>(null);
  const [showRateEventWarning, setShowRateEventWarning] = useState<boolean>(false);
  const [showDurationEventWarning, setShowDurationEventWarning] = useState<boolean>(false);
  const [areOtherBcbaAssignmentsPresent, setAreOtherBcbaAssignmentsPresent] =
    useState<boolean>(false);

  const showDeleteButton = useMemo(() => {
    return Boolean(
      !areUserRolesIn(user?.roles, [Roles.LimitedAccessGuardian]) &&
      session?.id &&
      session?.status === BTSessionStatus.Started &&
      (session?.session_provider?.email === user?.email ||
        areUserRolesIn(user?.roles, [Roles.Admin])),
    );
  }, [session, user]);

  const isSessionNotesV2Session =
    sessionNotesV2Enabled && session?.note_version === SESSION_NOTE_VERSIONS.V2;
  const isStartEvvPending = Boolean(
    isSessionNotesV2Session && session?.evv_required && !session.evv_start_captured,
  );
  const {
    closeEvvQrDialog,
    evvLoadingKind,
    startEvvVerification,
  } = useEvvVerification({
    sessionId: isSessionNotesV2Session && session?.evv_required ? session?.id : undefined,
  });

  const loadSelectedClientDetails = useSelectedClientDetailsLoader({
    clients,
    providerAssignments,
    getAccessTokenSilently,
  });

  const clientOptions = useMemo(() => {
    return session?.client
      ? // If session created exists
        [
          {
            id: session.client.id,
            label: `${session.client.first_name} ${session.client.last_name}`,
            description: '',
          },
        ]
      : // If BCBA show provider assigments (HOME BCBA)
        areUserRolesIn(user?.roles, [Roles.Bcba])
        ? (providerAssignments
            ?.filter((pa) => pa.client?.clinical_status === ClinicalStatus.CLINICAL)
            ?.map((pa) => ({
              id: pa.client?.id,
              label: `${pa.client?.first_name} ${pa.client?.last_name}`,
              description:
                pa.clinical_relationship === ClinicalRelationships.HomeBcba
                  ? ''
                  : pa.clinical_relationship?.split('_')[0],
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) ?? [])
        : // Else Show clients list
          (clients
            ?.map((client: IClient) => ({
              id: client.id,
              label: `${client.first_name} ${client.last_name}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) ?? []);
  }, [clients, session, user, providerAssignments]);

  const selectedClientDetails: IClient | null = useMemo(() => {
    if (!selectedClientId) return null;
    return clients.find((c) => c.id === selectedClientId) ?? null;
  }, [clients, selectedClientId]);

  const currentClientCohortAssignments = useMemo(() => {
    return getCurrentClientCohortAssignments(
      clientCohortAssignments,
      sessionStartDate,
      timeStartValue,
    );
  }, [clientCohortAssignments, sessionStartDate, timeStartValue]);

  const isBay6Active = useMemo(
    () =>
      getActiveBayAssignmentAtDateTime(
        clientCohortAssignments ?? [],
        BAYS.BAY_6,
        sessionStartDate,
        timeStartValue,
      ),
    [clientCohortAssignments, sessionStartDate, timeStartValue],
  );

  const showBcbaAttendanceField = useMemo(
    () => getShowBcbaAttendanceField(isBay6Active, areOtherBcbaAssignmentsPresent),
    [isBay6Active, areOtherBcbaAssignmentsPresent],
  );

  const {
    timeToolTipOpen,
    durationToolTipOpen,
    startBetween10to12,
    endBetween10to12,
    abbreviatedZone,
    zone,
    isTZIdentified,
  } = useProcedureTimeRestrictions(
    combineMomentDateTime(moment(sessionStartDate), moment(timeStartValue)),
    // An unselected duration keeps the warning math identical to the previous 3:00 hr default.
    selectedDuration ?? DEFAULT_SESSION_DURATION,
    Boolean((session && !isEditable) || areUserRolesIn(user?.roles, [Roles.Vbt])),
    clients.find((cl) => cl.id === selectedClientId)?.street_address_zip ??
      providerAssignments?.find((pa) => pa.client?.id === selectedClientId)?.client
        ?.street_address_zip ??
      null,
  );

  const durationLabels = Object.fromEntries(
    Object.entries(DurationOptions).map(([label, value]) => [value, label]),
  );

  useEffect(() => {
    if (!session) {
      setSelectedDuration(newSessionDefaultDuration);
      setBcbaAttendanceStatus(null);
      setLocation(null);
      setDeliveryMethod(
        areUserRolesIn(user?.roles, [Roles.Vbt])
          ? DeliveryMethods.TELEHEALTH
          : DeliveryMethods.IN_PERSON,
      );
      return;
    }

    setSelectedClientId(session.client_id);
    setSessionStartDate(moment(session.date_start));
    setTimeStartValue(moment(session.date_start));
    setSelectedDuration(session.expected_duration);
    setBcbaAttendanceStatus(session.is_supervised);
    setLocation(session.location);
    setDeliveryMethod(session.delivery_method);
  }, [session, user?.roles, newSessionDefaultDuration]);

  useEffect(() => {
    if (!session && (!clients || clients.length > 1)) {
      setSelectedClientId(null);
      return;
    }
  }, [session, clients]);

  useEffect(() => {
    if (!selectedClientId || !areUserRolesIn(user?.roles, [Roles.Bcba, Roles.Vbt])) return;
    dispatch(
      cohortActions.getClientCohortAssignments({
        params: { client_id: selectedClientId },
        getAccessTokenSilently,
      }),
    );
  }, [selectedClientId, user?.roles]);

  useUpdatedDeliveryMethod({
    cohortName: currentClientCohortAssignments?.cohort?.name,
    userRoles: user?.roles,
    currentDeliveryMethod: deliveryMethod,
    sessionId: session?.id,
    selectedClientId,
    setDeliveryMethod,
  });

  const bay6BasedGridSize = showBcbaAttendanceField ? 2.25 : 3.75;

  useEffect(() => {
    if (!selectedClientId || !areUserRolesIn(user?.roles, [Roles.Bcba]) || !isBay6Active) {
      setAreOtherBcbaAssignmentsPresent(false);
      return;
    }

    let cancelled = false;

    void (async () => {
      const count = await getNumberOfBcbaProviderAssignments(
        selectedClientId,
        getAccessTokenSilently,
      );
      if (!cancelled) {
        setAreOtherBcbaAssignmentsPresent(count > 0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedClientId, getAccessTokenSilently, user?.roles, isBay6Active]);

  const deliveryMethodOptions = useMemo(() => {
    return getDeliveryMethodOptions(
      user?.roles ?? [],
      currentClientCohortAssignments?.cohort?.name,
    );
  }, [user?.roles, currentClientCohortAssignments?.cohort?.name]);

  const disableByClientBay = useMemo(
    () =>
      shouldDisableSessionStartByClientBay(
        clientCohortAssignments,
        user?.roles,
        sessionStartDate,
        timeStartValue,
      ),
    [clientCohortAssignments, user?.roles, timeStartValue, sessionStartDate],
  );

  const handleClientChange = async (clientId: number | null = null) => {
    const id = clientId ? clientId : pendingSelectedClientId;
    setSelectedClientId(id);
    loadSelectedClientDetails(id);
  };

  const checkDurationEventsInRange = (newDuration: number) => {
    let isOutOfRange = false;
    const goalsAndTrials = session?.goals_and_trials;
    if (!goalsAndTrials || Array.isArray(goalsAndTrials)) return false;

    for (const { trials } of Object.values(goalsAndTrials)) {
      trials.forEach((trial: ISessionPracticedTargetTrial) => {
        const events = trial?.duration_events;
        const totalTrialDuration =
          events?.reduce((acc, duration) => acc + (duration?.duration ?? 0), 0) ?? 0;
        if (totalTrialDuration > newDuration) {
          isOutOfRange = true;
        }
      });
    }
    return isOutOfRange;
  };

  const checkRateEventsInRange = (startTime: Moment, duration: number) => {
    const endTime = moment(startTime).add(duration, 'seconds');
    let outOfRangeCount = 0;

    // Check all targets for rate events
    const goalsAndTrials = session?.goals_and_trials;
    if (!goalsAndTrials || Array.isArray(goalsAndTrials)) return 0;

    for (const { trials } of Object.values(goalsAndTrials)) {
      trials.forEach((trial: ISessionPracticedTargetTrial) => {
        const events = trial?.rate_events;
        events?.forEach((event) => {
          const eventTime = moment(event.date_of_event);
          if (eventTime.isSameOrBefore(startTime) || eventTime.isSameOrAfter(endTime)) {
            outOfRangeCount++;
          }
        });
      });
    }
    return outOfRangeCount;
  };

  const handleTimeRangeUpdate = (key: string, value: any) => {
    const newStartTime = key === 'date_start' ? value : session?.date_start;
    const newDuration = key === 'expected_duration' ? value : session?.expected_duration;

    const isDurationOutOfRange = checkDurationEventsInRange(newDuration);
    if (isDurationOutOfRange) {
      setShowDurationEventWarning(true);
      return;
    }

    const outOfRangeCount = checkRateEventsInRange(newStartTime, newDuration);
    if (outOfRangeCount > 0) {
      setShowRateEventWarning(true);
    } else {
      if (key === 'date_start') {
        handleDateTimePickerChange(value);
      }
      if (key === 'expected_duration') {
        handleDurationChange(value);
      }
    }
  };

  const handleDurationChange = async (value: number) => {
    if (session) {
      // Update session duration on changing dropdown
      const res = await dispatch(
        sessionActions.updateSessionHeaderInfo({
          sessionId: session.id,
          data: {
            utc_offset: getUtcOffset(moment(new Date())),
            expected_duration: value,
          },
          updateGPTNarrativeFlag: true,
          getAccessTokenSilently,
        }),
      );

      if (!res.payload) return;

      toast.success('Duration has been changed successfully');
    }

    setSelectedDuration(value);
  };

  const handleBcbaAttendanceChange = async (value: string) => {
    let newAttendanceStatus: boolean | null = null;
    if (value === BcbaAttendance.Present) {
      newAttendanceStatus = true;
    } else if (value === BcbaAttendance.Absent) {
      newAttendanceStatus = false;
    }
    setBcbaAttendanceStatus(newAttendanceStatus);

    if (session) {
      // Update bcba attendance status on changing dropdown
      const res = await dispatch(
        sessionActions.updateSessionHeaderInfo({
          sessionId: session.id,
          data: {
            utc_offset: getUtcOffset(moment(new Date())),
            is_supervised: newAttendanceStatus,
          },
          updateGPTNarrativeFlag: true,
          getAccessTokenSilently,
        }),
      );

      if (!res.payload) {
        setBcbaAttendanceStatus(session.is_supervised);
        return;
      }

      toast.success('BCBA attendance status has been changed successfully');
    }
  };

  const handleStartSession = () => {
    if (!selectedClientId) {
      toast.error('Please select a client to start new session');
      return;
    }

    if (!sessionStartDate) {
      toast.error('Please choose a start date for the session');
      return;
    }

    if (!timeStartValue) {
      toast.error('Please choose a start time for the the session');
      return;
    }

    if (!selectedDuration) {
      toast.error('Please choose a duration for the session');
      return;
    }

    if (combineMomentDateTime(sessionStartDate, timeStartValue)?.isAfter(moment(new Date()))) {
      toast.error('Start time cannot be in the future');
      return;
    }

    if (
      showBcbaAttendanceField &&
      bcbaAttendanceStatus !== true &&
      bcbaAttendanceStatus !== false
    ) {
      toast.error('Please choose whether BCBA attended the session');
      return;
    }

    if (!location) {
      toast.error('Please choose a location for the session');
      return;
    }

    if (!timeChanged) {
      setOpenConfirm(true);
      return;
    }

    handleSubmit();
  };

  const handleStartEvvCaptured = useCallback(async (sessionId: number | string) => {
    setEvvQrDialog(null);
    closeEvvQrDialog();
    window.history.replaceState(null, '', `/sessions/${sessionId}`);
    await dispatch(
      sessionActions.getSession({
        sessionId,
        getAccessTokenSilently,
      }),
    );
    setIsSessionStartLocked(false);
  }, [
    closeEvvQrDialog,
    dispatch,
    getAccessTokenSilently,
    setEvvQrDialog,
    setIsSessionStartLocked,
  ]);

  const handleSubmit = async () => {
    setOpenConfirm(false);
    if (!sessionStartDate) return;

    setStartSessionLoading(true);
    const response = await dispatch(
      sessionActions.startSession({
        data: {
          client_id: selectedClientId,
          date_start: combineMomentDateTime(
            moment(sessionStartDate),
            moment(timeStartValue),
          ).toISOString(),
          utc_offset: getUtcOffset(sessionStartDate),
          service_code_id: 1,
          expected_duration: selectedDuration,
          is_supervised: showBcbaAttendanceField ? bcbaAttendanceStatus === true : false,
          location,
          delivery_method: deliveryMethod,
          note_version: sessionNotesV2Enabled
            ? SESSION_NOTE_VERSIONS.V2
            : SESSION_NOTE_VERSIONS.V1,
        },
        getAccessTokenSilently,
      }),
    );

    if (response.error || !response.payload) {
      setStartSessionLoading(false);
      return;
    }

    const startedSession: IBTSession = response.payload;
    if (!startedSession.id) {
      setStartSessionLoading(false);
      return;
    }
    const startedSessionId = startedSession.id;

    if (startedSession.evv_required && !startedSession.evv_start_captured) {
      window.history.replaceState(null, '', `/sessions/${startedSessionId}`);
      setIsSessionStartLocked(true);
      const evvResponse = await startEvvVerification({
        kind: EVV_TOKEN_KINDS.START,
        sessionIdOverride: startedSessionId,
        onCaptured: () => {
          void handleStartEvvCaptured(startedSessionId);
        },
      });
      setStartSessionLoading(false);

      if (evvResponse.started) {
        if (!evvResponse.redirected && evvResponse.route && evvResponse.sessionId) {
          const evvSessionId = evvResponse.sessionId;
          // BTSession owns the start dialog and its poll; a second poll would replace its refreshed tokens.
          closeEvvQrDialog();
          setEvvQrDialog({
            kind: EVV_TOKEN_KINDS.START,
            route: evvResponse.route,
            sessionId: evvSessionId,
            onClose: handleCancelStartEvvVerification,
            onCaptured: () => {
              void handleStartEvvCaptured(evvSessionId);
            },
          });
        }
        return;
      }

      setIsSessionStartLocked(false);

      await dispatch(
        sessionActions.deleteSession({
          sessionId: startedSessionId,
          suppressToast: true,
          getAccessTokenSilently,
        }),
      );
      return;
    }

    // Need to use native browser feature for navigation here as react-router-dom will trigger a reload
    window.history.replaceState(null, '', `/sessions/${startedSessionId}`);
    setIsSessionStartLocked(false);
    setStartSessionLoading(false);
  };

  const handleDateTimePickerChange = async (newValue: Moment) => {
    // check if the time selected is greater then current time
    // this is used for edge cases for when a previous date was selected with
    // a later time then today. Then a user switches dates to today, with later time
    // being registred by the plug in. We want to shift that time to earliest possible
    if (moment(newValue, 'minute').isAfter(moment(new Date()), 'minute')) {
      const resetValue = moment(newValue).hours(0).minutes(0).seconds(0).milliseconds(0);
      setSessionStartDate(resetValue);
      setTimeChanged(true);
    } else {
      setSessionStartDate(newValue);
      setTimeChanged(true);
    }
  };

  const handleGetOutOnRangeCount = (value: Moment | null) => {
    if (value === null || !sessionStartDate) return null; // select date before time
    let val = value.seconds(0).milliseconds(0);

    const now = moment();

    if (value.isAfter(now)) {
      val = moment(); // Set value to current time, if value is in future.
    }
    const combinedDateTime = combineMomentDateTime(moment(sessionStartDate), val);
    return checkRateEventsInRange(combinedDateTime, session?.expected_duration ?? 0);
  };

  const handleTimeSelect = (value: Moment | null) => {
    if (value === null || !sessionStartDate) return; // select date before time
    let val = value.seconds(0).milliseconds(0);

    const now = moment();

    if (value.isAfter(now)) {
      val = moment(); // Set value to current time, if value is in future.
    }
    const combinedDateTime = combineMomentDateTime(moment(sessionStartDate), val);
    const outOfRangeCount = checkRateEventsInRange(
      combinedDateTime,
      session?.expected_duration ?? 0,
    );
    if (outOfRangeCount > 0) {
      setShowRateEventWarning(true);
    } else {
      setTimeStartValue(combinedDateTime);
      setSessionStartDate(combinedDateTime);
    }
  };

  const handleUpdateSessionTime = async (startDate: any) => {
    // Update session duration on changing dropdown if new time is different from date_start
    if (!session || moment(session?.date_start).isSame(moment(startDate), 'minute')) return;

    const outsideRageCount = handleGetOutOnRangeCount(moment(startDate));
    if (outsideRageCount !== null && outsideRageCount > 0) {
      setShowRateEventWarning(true);
      return;
    }

    const res = await dispatch(
      sessionActions.updateSessionHeaderInfo({
          sessionId: session.id,
          data: {
            utc_offset: getUtcOffset(startDate),
            date_start: combineMomentDateTime(
              moment(sessionStartDate),
            moment(timeStartValue),
          ).toISOString(),
        },
        updateGPTNarrativeFlag: true,
        getAccessTokenSilently,
      }),
    );

    if (!res.payload) {
      setSessionStartDate(moment(session.date_start));
      return;
    }
    toast.success('Start date and/or the time has been changed successfully');
  };

  const handleUpdateLocation = async (location: Locations) => {
    setLocation(location);

    if (session) {
      const res = await dispatch(
        sessionActions.updateSessionHeaderInfo({
          sessionId: session.id,
          data: {
            utc_offset: getUtcOffset(moment(new Date())),
            location,
          },
          updateGPTNarrativeFlag: true,
          getAccessTokenSilently,
        }),
      );

      if (!res.payload) {
        setLocation(session.location);
        return;
      }

      toast.success('Location has been changed successfully');
    }
  };

  const handleDeliveryMethodChange = async (deliveryMethod: DeliveryMethods) => {
    if (session) {
      const res = await dispatch(
        sessionActions.updateSessionHeaderInfo({
          sessionId: session.id,
          data: {
            utc_offset: getUtcOffset(moment(new Date())),
            delivery_method: deliveryMethod,
          },
          updateGPTNarrativeFlag: true,
          getAccessTokenSilently,
        }),
      );

      if (!res.payload) return;

      toast.success('Delivery method has been changed successfully');
    }
    setDeliveryMethod(deliveryMethod);
  };

  const handleDeleteSession = async () => {
    if (!session?.id) return;
    setShowConfirmDeleteDialog(false);

    const { error } = await dispatch(
      sessionActions.deleteSession({
        sessionId: session.id,
        getAccessTokenSilently,
      }),
    );

    if (error) return;
    history.navigate('/dashboard');
  };

  const handleCancelStartEvvVerification = () => {
    setEvvQrDialog(null);
    closeEvvQrDialog();
    setIsSessionStartLocked(true);
  };

  const handleStartEvvVerification = async () => {
    if (!session?.id || !isStartEvvPending) return;
    const evvSessionId = session.id;

    setIsSessionStartLocked(true);
    const evvResponse = await startEvvVerification({
      kind: EVV_TOKEN_KINDS.START,
      sessionIdOverride: evvSessionId,
      replaceActive: true,
      onCaptured: () => {
        void handleStartEvvCaptured(evvSessionId);
      },
    });

    if (
      evvResponse.started &&
      !evvResponse.redirected &&
      evvResponse.route &&
      evvResponse.sessionId
    ) {
      const responseSessionId = evvResponse.sessionId;
      closeEvvQrDialog();
      setEvvQrDialog({
        kind: EVV_TOKEN_KINDS.START,
        route: evvResponse.route,
        sessionId: responseSessionId,
        onClose: handleCancelStartEvvVerification,
        onCaptured: () => {
          void handleStartEvvCaptured(responseSessionId);
        },
      });
    }
  };

  const tooltipTitle = (
    <Box data-testid="tooltipTitle" sx={{ display: 'flex', alignItems: 'center' }}>
      <img
        style={{
          width: '20px',
          height: '20px',
          marginRight: '8px',
          marginTop: '-4px',
          filter: 'brightness(84%) contrast(189%) hue-rotate(145deg) saturate(300%)',
        }}
        src={AttentionIcon}
        alt="attention-icon"
      />
      {startBetween10to12 || endBetween10to12 ? (
        <Typography sx={{ fontSize: '16px', color: FORTA_BLACK }}>
          Attention: Sessions conducted between{' '}
          <b>
            <span style={{ color: FORTA_ERROR }}>{`10:00 p.m and 12:00 a.m ${
              isTZIdentified ? `in the client’s local time zone (${abbreviatedZone})` : ''
            } `}</span>
            are unlikely to be accepted by the insurance.
          </b>
        </Typography>
      ) : (
        <Typography sx={{ fontSize: '16px', color: FORTA_BLACK }}>
          Attention: Sessions conducted between{' '}
          <b>
            <span style={{ color: FORTA_ERROR }}>{`12:00 a.m and 4:00 a.m ${
              isTZIdentified ? `in the client’s local time zone (${abbreviatedZone}) ` : ''
            } `}</span>
            will not be accepted by the insurance.
          </b>
        </Typography>
      )}
    </Box>
  );

  if (!sessionNotesV2Enabled || (session && !isSessionNotesV2Session)) {
    return (
      <Box>
        <ExistingSessionInfoFields
          abbreviatedZone={abbreviatedZone}
          bay6BasedGridSize={bay6BasedGridSize}
          bcbaAttendanceStatus={bcbaAttendanceStatus}
          clientOptions={clientOptions}
          cohortAssignmentLoading={cohortAssignmentLoading}
          currentClientCohortAssignments={currentClientCohortAssignments}
          deliveryMethod={deliveryMethod}
          deliveryMethodOptions={deliveryMethodOptions}
          disableByClientBay={disableByClientBay}
          durationLabels={durationLabels}
          durationToolTipOpen={durationToolTipOpen}
          endBetween10to12={endBetween10to12}
          handleBcbaAttendanceChange={handleBcbaAttendanceChange}
          handleClientChange={handleClientChange}
          handleDeleteSession={handleDeleteSession}
          handleDeliveryMethodChange={handleDeliveryMethodChange}
          handleGetOutOnRangeCount={handleGetOutOnRangeCount}
          handleStartSession={handleStartSession}
          handleTimeRangeUpdate={handleTimeRangeUpdate}
          handleTimeSelect={handleTimeSelect}
          handleUpdateLocation={handleUpdateLocation}
          handleUpdateSessionTime={handleUpdateSessionTime}
          isBay6Active={isBay6Active}
          isEditable={isEditable}
          isTZIdentified={isTZIdentified}
          location={location}
          narrativeSuggestionLoading={narrativeSuggestionLoading}
          selectedClientDetails={selectedClientDetails}
          selectedClientId={selectedClientId}
          selectedDuration={selectedDuration ?? DEFAULT_SESSION_DURATION}
          session={session}
          sessionLoading={sessionLoading}
          sessionStartDate={sessionStartDate}
          setOpenClientConfirm={setOpenClientConfirm}
          setPendingSelectedClientId={setPendingSelectedClientId}
          setShowConfirmDeleteDialog={setShowConfirmDeleteDialog}
          setShowDurationEventWarning={setShowDurationEventWarning}
          setShowRateEventWarning={setShowRateEventWarning}
          showBcbaAttendanceField={showBcbaAttendanceField}
          showConfirmDeleteDialog={showConfirmDeleteDialog}
          showDeleteButton={showDeleteButton}
          showDurationEventWarning={showDurationEventWarning}
          showRateEventWarning={showRateEventWarning}
          startBetween10to12={startBetween10to12}
          startSessionLoading={startSessionLoading}
          timeStartValue={timeStartValue}
          timeToolTipOpen={timeToolTipOpen}
          tooltipTitle={tooltipTitle}
          user={user}
          zone={zone}
        />
        {!session && (
          <SessionInfoDialogs
            handleClientChange={handleClientChange}
            handleDeleteSession={handleDeleteSession}
            handleSubmit={handleSubmit}
            openClientConfirm={openClientConfirm}
            openConfirm={openConfirm}
            setOpenClientConfirm={setOpenClientConfirm}
            setOpenConfirm={setOpenConfirm}
            setShowConfirmDeleteDialog={setShowConfirmDeleteDialog}
            setShowDurationEventWarning={setShowDurationEventWarning}
            setShowRateEventWarning={setShowRateEventWarning}
            showConfirmDeleteDialog={showConfirmDeleteDialog}
            showDurationEventWarning={showDurationEventWarning}
            showRateEventWarning={showRateEventWarning}
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <NewSessionInfoFields
        bcbaAttendanceStatus={bcbaAttendanceStatus}
        clientOptions={clientOptions}
        cohortAssignmentLoading={cohortAssignmentLoading}
        currentClientCohortAssignments={currentClientCohortAssignments}
        deliveryMethod={deliveryMethod}
        deliveryMethodOptions={deliveryMethodOptions}
        disableByClientBay={disableByClientBay}
        durationLabels={durationLabels}
        durationToolTipOpen={durationToolTipOpen}
        endBetween10to12={endBetween10to12}
        handleBcbaAttendanceChange={handleBcbaAttendanceChange}
        handleClientChange={handleClientChange}
        handleDeliveryMethodChange={handleDeliveryMethodChange}
        handleGetOutOnRangeCount={handleGetOutOnRangeCount}
        handleStartEvvVerification={handleStartEvvVerification}
        handleStartSession={handleStartSession}
        handleTimeRangeUpdate={handleTimeRangeUpdate}
        handleTimeSelect={handleTimeSelect}
        handleUpdateLocation={handleUpdateLocation}
        handleUpdateSessionTime={handleUpdateSessionTime}
        isEditable={isEditable}
        isStartEvvPending={isStartEvvPending}
        location={location}
        narrativeSuggestionLoading={narrativeSuggestionLoading}
        selectedClientId={selectedClientId}
        selectedDuration={selectedDuration}
        session={session}
        sessionLoading={sessionLoading}
        sessionStartDate={sessionStartDate}
        setOpenClientConfirm={setOpenClientConfirm}
        setPendingSelectedClientId={setPendingSelectedClientId}
        setShowConfirmDeleteDialog={setShowConfirmDeleteDialog}
        setShowRateEventWarning={setShowRateEventWarning}
        showBcbaAttendanceField={showBcbaAttendanceField}
        showDeleteButton={showDeleteButton}
        startBetween10to12={startBetween10to12}
        evvStartLoading={evvLoadingKind === EVV_TOKEN_KINDS.START}
        startSessionLoading={startSessionLoading}
        timeStartValue={timeStartValue}
        timeToolTipOpen={timeToolTipOpen}
        tooltipTitle={tooltipTitle}
        user={user}
      />
      <SessionInfoDialogs
        handleClientChange={handleClientChange}
        handleDeleteSession={handleDeleteSession}
        handleSubmit={handleSubmit}
        openClientConfirm={openClientConfirm}
        openConfirm={openConfirm}
        setOpenClientConfirm={setOpenClientConfirm}
        setOpenConfirm={setOpenConfirm}
        setShowConfirmDeleteDialog={setShowConfirmDeleteDialog}
        setShowDurationEventWarning={setShowDurationEventWarning}
        setShowRateEventWarning={setShowRateEventWarning}
        showConfirmDeleteDialog={showConfirmDeleteDialog}
        showDurationEventWarning={showDurationEventWarning}
        showRateEventWarning={showRateEventWarning}
      />
    </Box>
  );
}

export default SessionInfo;
